import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { EmptyIndicator, PublishedProductText } from 'components/common';
import {
  Box,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import {
  AnswersProgress,
  CompletionStatus,
  ReadingProgress,
  TaskPointsProgress,
  VideoProgress
} from 'components/common/tasks';
import { CourseTitleWithInfo } from 'components/courses/common';

import type { Course, CourseProgress } from 'shared-types';

interface Props {
  courses: Pick<
    Course,
    | 'id'
    | 'endDate'
    | 'language'
    | 'languageVersions'
    | 'picture'
    | 'picturePosition'
    | 'slug'
    | 'startDate'
    | 'title'
  >[];
  coursesProgressData: CourseProgress;
  loading: boolean;
}

const GroupFollowUserCourses = ({
  courses,
  coursesProgressData,
  loading
}: Props) => {
  const { messages, theme } = useReduxState();

  return (
    <div className="courses">
      <Box loading={loading} padding={0} title={messages.groupsCourses}>
        {courses && courses.length ? (
          <BoxTable>
            <BoxTableHeader hideAtBreakpoint={breakpoints.large}>
              <BoxTableCell>{messages.commonCourse}</BoxTableCell>
              <BoxTableCell>{messages.tasksReading}</BoxTableCell>
              <BoxTableCell>{messages.tasksVideos}</BoxTableCell>
              <BoxTableCell>{messages.tasksAnswers}</BoxTableCell>
              <BoxTableCell>{messages.tasksTaskPoints}</BoxTableCell>
              <BoxTableCell>{messages.commonCompleted}</BoxTableCell>
            </BoxTableHeader>

            {courses.map((course) => (
              <BoxTableRow key={course.id}>
                <BoxTableCell cellBreakpoint={breakpoints.large}>
                  <div className="course">
                    <CourseTitleWithInfo
                      course={course}
                      linkToCourseAdminPage={true}
                      style={{ flexGrow: 1 }}
                      titleStyle={{
                        color: course.startDate
                          ? theme.textColor
                          : theme.textColorLighter
                      }}
                    >
                      <PublishedProductText
                        endDate={course.endDate as string}
                        startDate={course.startDate as string}
                      />
                    </CourseTitleWithInfo>
                  </div>
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.large}
                  label={messages.tasksReading}
                  shrink={true}
                >
                  <ReadingProgress
                    analyticsDisabledForAdmin={
                      coursesProgressData[course.id].analyticsDisabledForAdmin
                    }
                    discrepancyInCompletion={
                      coursesProgressData[course.id].discrepancyInCompletion
                        ?.readingProgress
                    }
                    readingProgress={
                      coursesProgressData[course.id].readingProgress
                    }
                    readingProgressMaxTime={
                      coursesProgressData[course.id].readingProgressMaxTime
                    }
                  />
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.large}
                  label={messages.tasksVideos}
                  shrink={true}
                >
                  <VideoProgress
                    analyticsDisabledForAdmin={
                      coursesProgressData[course.id].analyticsDisabledForAdmin
                    }
                    discrepancyInCompletion={
                      coursesProgressData[course.id].discrepancyInCompletion
                        ?.videoProgress
                    }
                    numberOfVideos={
                      coursesProgressData[course.id].numberOfVideos
                    }
                    videoProgress={coursesProgressData[course.id].videoProgress}
                    videosWatched={
                      coursesProgressData[course.id].videoProgressVideosWatched
                    }
                  />
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.large}
                  label={messages.tasksAnswers}
                  shrink={true}
                >
                  <AnswersProgress
                    numberOfAnswers={coursesProgressData[course.id].taskAnswers}
                    numberOfTasks={coursesProgressData[course.id].numberOfTasks}
                  />
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.large}
                  label={messages.tasksTaskPoints}
                  shrink={true}
                >
                  <TaskPointsProgress
                    discrepancyInCompletion={
                      coursesProgressData[course.id].discrepancyInCompletion
                        ?.taskPointsProgress
                    }
                    numberOfTasks={coursesProgressData[course.id].numberOfTasks}
                    taskMaxPoints={coursesProgressData[course.id].taskMaxPoints}
                    taskPoints={coursesProgressData[course.id].taskPoints}
                    taskPointsProgress={
                      coursesProgressData[course.id].taskPointsProgress
                    }
                  />
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.large}
                  label={messages.commonCompleted}
                  shrink={true}
                >
                  <CompletionStatus
                    completion={coursesProgressData[course.id].completion}
                    course={course}
                    discrepancyInCompletion={
                      coursesProgressData[course.id].discrepancyInCompletion
                    }
                    progressData={coursesProgressData[course.id]}
                  />
                </BoxTableCell>
              </BoxTableRow>
            ))}
          </BoxTable>
        ) : (
          <EmptyIndicator icon="book" title={messages.commonNoCourses} />
        )}
      </Box>
    </div>
  );
};

export default GroupFollowUserCourses;
